import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import { ReactNode, useEffect, useRef, useState } from 'react';

const THRESHOLD = 70; // これ以上引いたら更新
const MAX_PULL = 120; // 見た目の最大引っ張り量
const RESISTANCE = 0.5; // 引っ張り抵抗（実移動 × この値）
const MIN_SPIN = 600; // スピナーを最低この時間は見せて「更新した」感を出す (ms)

const wait = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

type Props = {
  children: ReactNode;
  /**
   * 更新処理。Promise を返せば完了までスピナーを回し続ける。
   * 未指定なら全体リロード（standalone PWA でブラウザ更新が使えない対策）。
   */
  onRefresh?: () => void | Promise<void>;
};

/**
 * 画面トップでの引っ張り更新（pull-to-refresh）。
 * standalone PWA ではブラウザのリロード UI が無いため、トップで下に引くと更新できるようにする。
 *
 * コンテンツ（このラッパ配下）を transform で下に動かして native な引っ張り感を出す。
 * transform は配下の `position: fixed` の包含ブロックを作ってしまうので、固定したい
 * BottomNav 等はこのラッパの「外（兄弟）」に置くこと（MobileShell 参照）。
 * 引っ張っていない時は transform: none にして包含ブロックを作らない。
 */
export const PullToRefresh = ({ children, onRefresh }: Props) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef<number | null>(null);
  const pullingRef = useRef(false);
  const pullRef = useRef(0);
  const refreshingRef = useRef(false);
  const [pull, setPull] = useState(0);
  const [refreshing, setRefreshing] = useState(false);

  const setPullValue = (d: number) => {
    pullRef.current = d;
    setPull(d);
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const atTop = () => (window.scrollY || document.documentElement.scrollTop || 0) <= 0;

    const onStart = (e: TouchEvent) => {
      if (refreshingRef.current) return;
      if (atTop() && e.touches.length === 1) {
        startYRef.current = e.touches[0].clientY;
        pullingRef.current = false;
      } else {
        startYRef.current = null;
      }
    };

    const onMove = (e: TouchEvent) => {
      if (refreshingRef.current || startYRef.current === null) return;
      const dy = e.touches[0].clientY - startYRef.current;
      if (dy > 0 && atTop()) {
        pullingRef.current = true;
        setPullValue(Math.min(dy * RESISTANCE, MAX_PULL));
        // トップでの下方向の引っ張りはページのバウンス/スクロールを抑止して更新ジェスチャにする
        e.preventDefault();
      } else if (pullingRef.current) {
        pullingRef.current = false;
        setPullValue(0);
      }
    };

    const onEnd = () => {
      if (refreshingRef.current) return;
      if (pullingRef.current && pullRef.current >= THRESHOLD) {
        refreshingRef.current = true;
        setRefreshing(true);
        setPullValue(THRESHOLD);
        const run = onRefresh ?? (() => window.location.reload());
        // onRefresh が Promise を返せば完了まで待つ。最低 MIN_SPIN は回す。
        // 全体リロード時はそのままページが差し替わるので finally には到達しない。
        Promise.all([Promise.resolve(run()), wait(MIN_SPIN)]).finally(() => {
          refreshingRef.current = false;
          setRefreshing(false);
          setPullValue(0);
        });
      } else {
        setPullValue(0);
      }
      startYRef.current = null;
      pullingRef.current = false;
    };

    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: false });
    el.addEventListener('touchend', onEnd, { passive: true });
    el.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [onRefresh]);

  const offset = refreshing ? THRESHOLD : pull;
  const ready = pull >= THRESHOLD;
  const noTransition = pullingRef.current;

  return (
    <Box ref={containerRef} sx={{ position: 'relative' }}>
      {/* 引っ張りで開く上部の隙間に出すインジケータ */}
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          pointerEvents: 'none',
          zIndex: (t) => t.zIndex.appBar + 2,
          transform: `translateY(${offset - 44}px)`,
          opacity: offset > 0 ? 1 : 0,
          transition: noTransition ? 'none' : 'transform .25s ease, opacity .25s ease',
        }}
      >
        <CircularProgress
          size={28}
          thickness={4}
          variant={refreshing ? 'indeterminate' : 'determinate'}
          value={Math.min((pull / THRESHOLD) * 100, 100)}
          // 青い隙間の上に白いスピナーを直接出す（台座なしですっきり）
          sx={{ color: 'common.white', opacity: ready || refreshing ? 1 : 0.65 }}
        />
      </Box>

      {/* コンテンツ。引っ張り量だけ下にずらして「画面が引っ張られる」感を出す。
          pull=0 の時は transform を none にして fixed 要素の包含ブロックを作らない。 */}
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          transform: offset > 0 ? `translateY(${offset}px)` : 'none',
          transition: noTransition ? 'none' : 'transform .25s ease',
          // will-change: transform も fixed の包含ブロックを作るため、引っ張り中だけ付ける。
          // idle 時に付けっぱなしだと配下の Snackbar/Backdrop(position:fixed) がビューポート基準で
          // なくなり、画面下端(スクロールしないと見えない位置)に出てしまう。
          willChange: offset > 0 ? 'transform' : 'auto',
        }}
      >
        {/* 引っ張りで開く隙間を埋める青帯。コンテンツ上端（bottom:100%）に貼り付けて
            一緒に transform で動かすので、引いている間も手を離した戻りアニメ中も
            常に隙間が青で埋まり、境界に切れ目が出ない。TopBar(不透明)の上に置くため
            本文の透過部分に青が漏れることもない。 */}
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            bottom: '100%',
            left: 0,
            right: 0,
            height: MAX_PULL + 200,
            bgcolor: 'primary.main',
          }}
        />
        {children}
      </Box>
    </Box>
  );
};
